import React, { useEffect, useState } from "react";
import { Menu, X, ShoppingBag, User, Shield, LogOut } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const links = [
  { label: "Menu", href: "#menu" },
  { label: "Drinks", href: "#beverages" },
  { label: "Order", href: "#order" },
  { label: "Delivery", href: "#delivery" },
  { label: "Catering", href: "#catering" },
  { label: "Reservations", href: "#reservation" },
  { label: "Stories", href: "/blog" },
  { label: "Contact", href: "#contact" }
];

const drawerLinks = [
  ...links.slice(0, 6),
  { label: "Bundles", href: "#bundles" },
  { label: "Reviews", href: "#reviews" },
  { label: "Community", href: "#community" },
  ...links.slice(6)
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { count, setOpen: setCartOpen } = useCart();
  const { user, isAdmin, isAuthenticated, setModal, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Over the hero video the nav text is light; once scrolled it sits on sand.
  const txt = scrolled ? "text-[var(--mm-cream)]" : "text-white/90";
  const txtHover = scrolled ? "hover:text-[var(--mm-accent)]" : "hover:text-white";

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#F6F5F2]/90 backdrop-blur-md py-3 border-b border-[rgba(26,26,26,0.08)]"
            : "bg-transparent py-6"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex items-center justify-between">
          <a href="#top" className="flex items-center flex-shrink-0" data-testid="nav-brand">
            <img
              src="/media/logo-300.png"
              alt="Mamamina's Soulfeed"
              width="56"
              height="56"
              className="h-12 md:h-14 w-auto object-contain"
              style={{ maxWidth: "none" }}
            />
          </a>

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className={`text-[0.68rem] tracking-[0.26em] uppercase ${txt} ${txtHover} transition-colors duration-300`}
              >
                {l.label}
              </a>
            ))}
            <a
              href="#reservation"
              className={`text-[0.78rem] tracking-[0.12em] lowercase border-b pb-0.5 transition-colors duration-300 ${
                scrolled
                  ? "text-[var(--mm-cream)] border-[rgba(26,26,26,0.4)] hover:border-[var(--mm-cream)]"
                  : "text-white border-white/50 hover:border-white"
              }`}
            >
              book a table
            </a>
          </nav>

          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <div className="relative hidden sm:block">
                <button
                  onClick={() => setUserMenuOpen((v) => !v)}
                  aria-label="Account menu"
                  className={`flex items-center gap-2 px-3 py-2 ${txt} ${txtHover} transition-colors`}
                >
                  <User size={18} />
                  <span className="text-xs tracking-[0.2em] uppercase hidden md:inline">
                    {user.name?.split(" ")[0] || "Account"}
                  </span>
                </button>
                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute right-0 top-full mt-2 w-52 bg-[var(--mm-bg)] border border-[var(--mm-line)] py-2 z-20 shadow-lg">
                      <Link
                        to="/account"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-[var(--mm-cream)] hover:bg-[var(--mm-bg-alt)] hover:text-[var(--mm-accent)] transition-colors"
                      >
                        <User size={14} /> My Account
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-[var(--mm-cream)] hover:bg-[var(--mm-bg-alt)] hover:text-[var(--mm-accent)] transition-colors"
                        >
                          <Shield size={14} /> Admin Dashboard
                        </Link>
                      )}
                      <button
                        onClick={() => { logout(); setUserMenuOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[var(--mm-cream)] hover:bg-[var(--mm-bg-alt)] hover:text-[var(--mm-accent)] transition-colors"
                      >
                        <LogOut size={14} /> Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button
                onClick={() => setModal("login")}
                aria-label="Sign in"
                data-testid="nav-sign-in"
                className={`inline-flex items-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2 border transition-colors text-[0.65rem] sm:text-xs tracking-[0.2em] uppercase ${
                  scrolled
                    ? "border-[rgba(26,26,26,0.35)] text-[var(--mm-cream)] hover:bg-[var(--mm-cream)] hover:text-[var(--mm-bg)]"
                    : "border-white/50 text-white hover:bg-white hover:text-[#1A1A1A]"
                }`}
              >
                <User size={14} />
                <span>Sign In</span>
              </button>
            )}

            <button
              onClick={() => setCartOpen(true)}
              aria-label="Open cart"
              className={`relative ${txt} ${txtHover} transition-colors p-2`}
            >
              <ShoppingBag size={20} />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[var(--mm-accent)] text-[#F6F5F2] text-[0.6rem] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {count}
                </span>
              )}
            </button>

            <button
              aria-label="Open menu"
              className={`lg:hidden ${txt}`}
              onClick={() => setOpen(true)}
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] bg-[#F6F5F2] transition-transform duration-500 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex justify-between items-center p-6">
          <img
            src="/media/logo-300.png"
            alt="Mamamina's Soulfeed"
            className="h-14 w-auto object-contain"
          />
          <button onClick={() => setOpen(false)} className="text-[var(--mm-cream)]" aria-label="Close">
            <X size={28} />
          </button>
        </div>
        <nav className="flex flex-col items-start gap-6 px-8 pt-8 overflow-y-auto max-h-[calc(100vh-100px)] pb-16">
          {drawerLinks.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={() => setOpen(false)}
              className="font-display text-4xl text-[var(--mm-cream)] hover:text-[var(--mm-accent)] transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a href="#reservation" onClick={() => setOpen(false)} className="editorial-link mt-4 text-lg">
            book a table
          </a>

          {isAuthenticated ? (
            <div className="mt-6 flex flex-col gap-3 w-full">
              <Link
                to="/account"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 text-[var(--mm-cream)] text-lg"
              >
                <User size={16} /> My Account
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 text-[var(--mm-cream)] text-lg"
                >
                  <Shield size={16} /> Admin
                </Link>
              )}
              <button
                onClick={() => { logout(); setOpen(false); }}
                className="flex items-center gap-3 text-[var(--mm-cream-muted)] text-lg"
              >
                <LogOut size={16} /> Sign Out
              </button>
            </div>
          ) : (
            <button
              onClick={() => { setModal("login"); setOpen(false); }}
              className="mt-6 flex items-center gap-3 text-[var(--mm-cream)] text-lg"
            >
              <User size={16} /> Sign In / Create Account
            </button>
          )}
        </nav>
      </div>
    </>
  );
}
